/*
	AWSExceptionsApi.ts -- AWS-specific interface to making Exceptions related network calls.
*/
import { EXCEPTIONS, HISTORY } from 'Config/Paths';
import { CreateExceptionRequest, ExceptionHistoryResponse, ExceptionResponse, UpdateExceptionRequest } from 'Models/Exceptions';
import { DueDiligenceModule, ProductModule } from 'Models/ProductModule';
import { ResponseModel } from 'Models/ResponseModel';
import { restApiDelete, restApiGet, restApiPut } from 'RestApiConnector';

import { ExceptionsApi } from './ExceptionsApi';

export class AWSExceptionsApi implements ExceptionsApi {
    /**
     * @param module The due diligence module whose exceptions `getAllDueDiligenceExceptions` returns; no other method uses it. Defaults to TPRM; `App.tsx` shares one default instance between Controls and TPRM pages, and creates a separate instance for UCRM.
     */
    constructor(private module: DueDiligenceModule = ProductModule.TPRM) {}

    getAllExceptions = (): Promise<ResponseModel<ExceptionResponse[]>> => {
        return restApiGet(`/${EXCEPTIONS}`, {});
    };
    getAllControlExceptions = (): Promise<ResponseModel<ExceptionResponse[]>> => {
        return restApiGet(`/${EXCEPTIONS}`, { exception_type: ProductModule.CONTROLS });
    };
    getAllDueDiligenceExceptions = (): Promise<ResponseModel<ExceptionResponse[]>> => {
        return restApiGet(`/${EXCEPTIONS}`, { exception_type: this.module });
    };
    getExceptionsByControl = (controlIdentifier: string): Promise<ResponseModel<ExceptionResponse[]>> => {
        return restApiGet(`/${EXCEPTIONS}`, { control: controlIdentifier });
    };
    getExceptionsByOrganization = (organizationId: string): Promise<ResponseModel<ExceptionResponse[]>> => {
        return restApiGet(`/${EXCEPTIONS}`, { organization_id: organizationId });
    };
    getException = (id: string): Promise<ResponseModel<ExceptionResponse>> => {
        return restApiGet(`/${EXCEPTIONS}/${id}`, {});
    };
    getExceptionHistory = (issueId: string): Promise<ResponseModel<ExceptionHistoryResponse[]>> => {
        return restApiGet(`/${EXCEPTIONS}/${HISTORY}/${issueId}`, {});
    };
    createException = (createExceptionRequest: CreateExceptionRequest): Promise<ResponseModel<ExceptionResponse>> => {
        return restApiPut(`/${EXCEPTIONS}`, createExceptionRequest);
    };
    updateException = (id: string, updateExceptionRequest: UpdateExceptionRequest): Promise<ResponseModel<ExceptionResponse>> => {
        return restApiPut(`/${EXCEPTIONS}/${id}`, updateExceptionRequest);
    };
    deleteException = (id: string): Promise<ResponseModel<void>> => {
        return restApiDelete(`/${EXCEPTIONS}/${id}`);
    };
}
